import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import cn from 'classnames';
import { useCallback, useEffect } from 'react';
import { Mosaic, MosaicBranch } from 'react-mosaic-component';
import { useNavigate } from 'react-router';
import Layout from './components/layout/Layout';
import OrderModal from './components/modal/OrderModal';
import BlottersModule from './components/modules/Blotters/BlottersModule';
import ChartModule from './components/modules/Chart/ChartModule';
import OrderModule from './components/modules/Order/OrderModule';
import OrderBookModule from './components/modules/OrderBook/OrderBookModule';
import FeatureModule from './components/mosaic/FeatureModule';
import HeadlessModule from './components/mosaic/HeadlessModule';
import { addMarketFull } from './services/MarketService';
import { useAppDispatch, useAppSelector } from './state/hooks';
import { controlInitSubscriptions, selectCredentials } from './state/reducers/authSlice';
import { getBlotterItemsThunk, getBlotterTradesThunk } from './state/reducers/blotterSlice';
import { fetchUserMarkets, PairMap, setActivePair } from './state/reducers/marketPairSlice';
import { setIsResizing } from './state/reducers/mosaicSlice';
import { setXplorSpotWorkspaceTabLayout } from './state/reducers/workspaceSlice';
import { store } from './state/store';
import useOrderDisclosure from './utils/hooks/useOrderDisclosure';
import useXplorSpotLayout from './utils/hooks/useXplorSpotLayout';
import { isCorrectLayout } from './utils/route';

const xplorspotEnabled = window.config?.layout?.xplorspot?.enabled;

export const initActivePair = () => {
    const state = store.getState();
    const dispatch = store.dispatch;

    const currentPair = state.marketPair.activePair?.netdania;
    const pairs = state.marketPair.pairs;
    if (!currentPair || !pairs.find((pair) => pair.netdania === currentPair)) {
        const primary = pairs.find((pair) => pair.netdania === 'BTC/USD');
        const secondary = pairs.find((pair) => pair.netdania === 'EUR/USD');
        const activePair = primary || secondary || pairs[0];
        dispatch(setActivePair(activePair));
        return activePair;
    } else {
        dispatch(setActivePair(state.marketPair.activePair as PairMap));
        return state.marketPair.activePair;
    }
};

function XplorSpot({ prevPath }: { prevPath: string | null }) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const credentials = useAppSelector(selectCredentials);

    const orderDisclosure = useOrderDisclosure();

    const screen = useScreenSizes();
    const { workspace, config } = useXplorSpotLayout();

    const renderer = useCallback(
        (id: string, path: MosaicBranch[], isBaseStructure) => {
            let moduleKey = Object.keys(config).find((key) => id.includes(key));
            if (id.includes('Order Book')) moduleKey = 'Order Book';
            if (moduleKey) {
                const props = {
                    path,
                    tradingModule: config[moduleKey],
                    orderBookModule: config[moduleKey],
                    isBaseStructure
                };

                if (id.includes('Init')) return <HeadlessModule id={id} {...props} />;
                else if (id.includes('Chart')) return <ChartModule id={id} {...props} />;
                else if (id.includes('Book')) return <OrderBookModule id={id} {...props} />;
                else if (id.includes('Blotter')) return <BlottersModule id={id} {...props} />;
                else if (id.includes('Order')) return <OrderModule id={id} {...props} />;
                else return <FeatureModule id={id} {...props} />;
            } else return <FeatureModule id={id} path={path} isBaseStructure />;
        },
        [config]
    );

    const renderTile = useCallback((id: string, path: MosaicBranch[]) => renderer(id, path, true), [renderer]);
    const renderEnlargedTile = useCallback((id: string, path: MosaicBranch[]) => renderer(id, path, false), [renderer]);

    useEffect(() => {
        if (!credentials) {
            navigate('/login');
        } else {
            if (isCorrectLayout('xplorspot')) {
                if (xplorspotEnabled) {
                    // Fire initial requests
                    dispatch(controlInitSubscriptions());
                    dispatch(fetchUserMarkets()).then(() => {
                        const activePair = initActivePair() as PairMap;
                        addMarketFull(activePair?.celer);
                        dispatch(getBlotterItemsThunk()).then(() => dispatch(getBlotterTradesThunk()));
                    });
                } else {
                    navigate('/lite');
                }
            } else {
                navigate('/trader');
            }
        }
    }, []);

    return credentials ? (
        <Layout prevPath={prevPath}>
            <div
                className={cn('absolute z-0 inset-0 h-full w-full transition-all duration-300', {
                    'opacity-50 -z-[1]': workspace.enlargedModule
                })}>
                <Mosaic<string>
                    renderTile={renderTile}
                    value={workspace.layout[screen.current] || 'Init'}
                    initialValue={workspace.layout[screen.current] || 'Init'}
                    onChange={(node) => {
                        dispatch(setXplorSpotWorkspaceTabLayout({ id: workspace.id, layout: node, screen: 'desktop' }));
                        dispatch(setIsResizing(true));
                    }}
                    onRelease={() => dispatch(setIsResizing(false))}
                    resize={{ minimumPaneSizePercentage: 5 }}
                />
            </div>
            <div
                className={cn('absolute inset-0 h-full w-full transition-all duration-300', {
                    'opacity-100 scale-100 z-[1]': workspace.enlargedModule,
                    'opacity-25 scale-50 -z-[1]': !workspace.enlargedModule
                })}>
                <Mosaic<string>
                    renderTile={renderEnlargedTile}
                    value={workspace.enlargedModule}
                    initialValue={workspace.enlargedModule || ''}
                />
            </div>
            <OrderModal opened={orderDisclosure[0]} handlers={orderDisclosure[1]} />
        </Layout>
    ) : null;
}

export default XplorSpot;
